import Collapse from '@material-ui/core/Collapse'
import Grid from '@material-ui/core/Grid'
import LinearProgress from '@material-ui/core/LinearProgress'
import {
  FormGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  InputLabel,
  Radio,
  RadioGroup,
  OutlinedInput,
} from '@material-ui/core'
import MuiAlert from '@mui/material/Alert'
import { DEFAULTS } from './constants'
import RrButton from '../RrButton'
import CsvPreview from './CsvPreview'
import { getCsvSample, validateCsv, nextStep, prevStep } from './helpers'
import { ExportToCsv } from 'export-to-csv'
import Papa from 'papaparse'

const Review = ({
  show,
  type,
  step,
  paragraph,
  csv,
  schema,
  onCancel,
  onValidated,
  listName,
  setListName,
  uploadSupporters,
  setUploadSupporters,
  needsList = false,
  confirmedColumns,
  onCsvUpdated,
}) => {
  const [header, setHeader] = useState()
  const [preview, setPreview] = useState()
  const [processing, setProcessing] = useState(false)
  const [progress, setProgress] = useState(0)
  const [valid, setValid] = useState()
  const [showingErrors, setShowingErrors] = useState(false)
  const [errorRows, setErrorRows] = useState([])
  const [validRows, setValidRows] = useState([])
  const [failed, setFailed] = useState(false)
  const [failedReason, setFailedReason] = useState(false)
  const [makeList, setMakeList] = useState(needsList)
  const [isRemovingInvalidRows, setIsRemovingInvalidRows] = useState(false)
  const targetKinds = window.ENV.SETTINGS['target_kinds'] || ['members', 'supporters']

  useEffect(() => {
    if (csv)
      getCsvSample(csv, 5).then((sample) => {
        setHeader(sample.data.slice(0, 1)[0])
        setPreview(sample.data.slice(1, 4))
      })
  }, [csv])

  useEffect(() => {
    if (processing && csv) {
      setTimeout(() => {
        // Run validation row by row
        validateCsv({ type, csv, schema, confirmedColumns })
          .then((result) => {
            setValid(!result.errorRows.length)
            setErrorRows(result.errorRows)
            setValidRows(result.validRows)
            setShowingErrors(result.errorRows.length > 0)
          })
          .catch((err) => {
            setFailed(true)
            setFailedReason(err.reason)
          })
          .finally(() => setTimeout(() => setProcessing(false), 500))
      }, 500)
    }
  }, [processing, csv])

  useEffect(() => {
    if (show && !isRemovingInvalidRows) {
      setProcessing(true)
      setFailed()
      setValid()
      setProgress(0)
      setPreview()
      window.addEventListener('csvProcessing', (evt) => {
        setProgress((100 * evt.loaded) / evt.total)
      })
    }
  }, [show, isRemovingInvalidRows])

  function canNext() {
    if (!valid && !failed) return false

    if (needsList) {
      return makeList && listName
    } else {
      if (makeList) {
        return !!listName
      } else {
        return true
      }
    }
  }

  const shouldProceed = () => {
    return canNext() && (!failed || valid)
  }

  function formattedMessage() {
    if (valid) {
      return <p>{paragraph || DEFAULTS.paragraphs.review}</p>
    } else {
      return (
        <MuiAlert
          severity="error"
          variant="outlined"
          sx={{ marginBottom: 2, marginTop: 2 }}
        >
          {failed
            ? failedReason
            : `Something is not right with your data. ${
                errorRows.length
              } rows are invalid. The following ${Math.min(
                errorRows.length,
                10000
              )} rows are invalid, because they are either missing data, or the data is in the incorrect format. Please look closely at the preview and then try again:`}
        </MuiAlert>
      )
    }
  }

  function download() {
    const csvExporter = new ExportToCsv({ filename: 'invalid-rows' })
    csvExporter.generateCsv([header, ...errorRows.map((row) => row.data)])
  }

  const removeInvalidRows = async () => {
    setIsRemovingInvalidRows(true)

    try {
      const csvContent = Papa.unparse([header, ...validRows.map(row => row.data)], {
        quotes: true,
        delimiter: ','
      })

      const cleanedRef = csv.ref instanceof File 
        ? new File([csvContent], csv.ref.name || 'cleaned-data.csv', { type: 'text/csv' })
        : new Blob([csvContent], { type: 'text/csv' })

      setErrorRows([])
      setValidRows([])
      setShowingErrors(false)
      setValid(undefined)
      setFailed(false)
      setFailedReason(false)
      setIsRemovingInvalidRows(false)

      onCsvUpdated?.({ ...csv, ref: cleanedRef })

      setProcessing(true)

    } catch (error) {
      console.error('Error removing invalid rows:', error)
      setFailed(true)
      setFailedReason('Failed to remove invalid rows. Please try again.')
      setIsRemovingInvalidRows(false)
    }
  }

  const handleRadioChange = (event) => {
    const { value } = event.target

    if (value === 'members') {
      setUploadSupporters(false)
    } else {
      setUploadSupporters(true)
    }
  }

  return (
    <Collapse in={show} className="cc-content-container">
      <Grid item container direction="column" spacing={2}>
        {!processing && (
          <Grid item>
            {formattedMessage()}
            <div className="flex">
              {show && !failed && !valid && (
                <>
                  <button className="button primary small" onClick={download}>
                    <SvgIconsDownload />
                    Download invalid rows
                  </button>
                  <button
                    className="button secondary small"
                    onClick={removeInvalidRows}
                    disabled={validRows.length === 0 || isRemovingInvalidRows}
                  >
                    <SvgIconsApprovedDocument />
                    Continue with invalid rows removed
                  </button>
                </>
              )}
            </div>
          </Grid>
        )}
        {!processing && header && (
          <>
            <Grid item style={{ maxWidth: '100%' }}>
              <CsvPreview
                header={header}
                csv={showingErrors ? errorRows.slice(0, 10000) : preview}
                showingErrors={showingErrors}
              />
            </Grid>
            {valid && (
              <Grid item>
                <div>
                  { type === 'upload-people' && targetKinds.includes('members') && <>
                    <h3>Who's in this upload?</h3>

                    <p>
                      You can upload these people as either members or supporters. Members
                      will be added to your core membership list. Supporters are separated
                      from your membership list, so that you can contact them as part of
                      external campaigns or communications.
                    </p>
                    <RadioGroup
                      aria-labelledby="radio-buttons-group-label"
                      name="radio-buttons-group"
                      onChange={handleRadioChange}
                      value={uploadSupporters ? 'supporters' : 'members'}
                    >
                      <FormControlLabel
                        value="members"
                        control={<Radio />}
                        label="Upload people as members"
                      />
                      <FormControlLabel
                        value="supporters"
                        control={<Radio />}
                        label="Upload people as supporters"
                      />
                    </RadioGroup>
                  </> }

                  { (type === 'upload-people' || type === 'upload-custom') && <div>
                    <h3>Create a list from the people in this upload</h3>
                    <RadioGroup
                      aria-labelledby="radio-buttons-group-label"
                      name="radio-buttons-group"
                      onChange={(e) => setMakeList(e.target.value === 'true')}
                      value={makeList ? 'true' : 'false'}
                    >
                      <FormControlLabel
                        value="true"
                        control={<Radio />}
                        label="Yes"
                      />
                      <FormControlLabel
                        value="false"
                        control={<Radio />}
                        label="No"
                      />
                    </RadioGroup>
                  </div> }

                  { makeList && (
                    <FormControl>
                      <InputLabel
                        htmlFor="list-name-input"
                        classes={{ root: '--custom-outline-label' }}
                      >
                        List name
                      </InputLabel>
                      <OutlinedInput
                        onChange={({ target: { value } }) => setListName(value)}
                        size="small"
                        placeholder="List name"
                        type="mui-input"
                        value={listName || ''}
                        classes={{
                          input: '--custom-outline-input',
                          notchedOutline: '--custom-outline-fieldset',
                        }}
                      />
                    </FormControl>
                  )}
                </div>
              </Grid>
            )}
          </>
        )}
        <Grid item style={{ padding: processing ? 16 : 0 }}>
          <Collapse in={processing}>
            <h4 style={{ marginBottom: 5 }}>Running data validation...</h4>
            <LinearProgress
              variant={progress ? 'determinate' : 'indeterminate'}
              value={progress}
              classes={{ root: 'avds-monitor', bar: 'avds-monitor-bar' }}
            />
          </Collapse>
        </Grid>
        <Grid
          item
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            paddingTop: 25,
          }}
        >
          <RrButton
            title={prevStep(step)}
            onClick={onCancel}
            type="secondary"
            icon="back"
            disabled={processing}
          />
          <RrButton
            title={nextStep(step)}
            onClick={shouldProceed() ? onValidated : () => {}}
            type="primary"
            disabled={!shouldProceed()}
            style={{
              pointerEvents: shouldProceed() ? 'auto' : 'none',
              opacity: shouldProceed() ? 1 : 0.5,
            }}
          />
        </Grid>
      </Grid>
    </Collapse>
  )
}

export default Review
